import { z } from "zod";
import { flag, lenientArray, strings, text } from "./common";

/**
 * The Life tab's four readings, as the backend names them. The server is the
 * authority; this list only stops the client asking for one that does not
 * exist. The fourth is `timing` on the wire and "when to make the move" in
 * the product.
 */
export const LIFE_READINGS = ["money", "year", "love", "timing"] as const;
export type LifeReadingName = (typeof LIFE_READINGS)[number];

export function isLifeReadingName(value: string): value is LifeReadingName {
  return (LIFE_READINGS as readonly string[]).includes(value);
}

/** One row of `GET /me/life` — a menu entry, never content. */
export const lifeRowSchema = z.object({
  reading: text(),
  title: text(),
  subtitle: text(),
  // Unknown means locked: guessing the other way shows paid content to
  // someone who has not paid for it.
  locked: flag(true),
});
export type LifeRow = Readonly<z.output<typeof lifeRowSchema>>;

export const lifeIndexSchema = z.object({
  entitled: flag(false),
  rows: lenientArray(lifeRowSchema),
});
export type LifeIndex = Readonly<z.output<typeof lifeIndexSchema>>;

export const lifeWindowSchema = z.object({
  level: text(),
  lord: text(),
  start_utc: text(),
  end_utc: text(),
  starts: text(),
  ends: text(),
  label: text(),
  running: flag(),
  what: text(),
});
export type LifeWindow = Readonly<z.output<typeof lifeWindowSchema>>;

/** `GET /me/life/{reading}`: one reading, computed fresh by the engine. */
export const lifeReadingSchema = z.object({
  reading: text(),
  headline: text(),
  framing: text(),
  engine_version: text(),
  windows: lenientArray(lifeWindowSchema),
  notes: strings(),
  facts_used: strings(),
  sources: strings(),
});
export type LifeReading = Readonly<z.output<typeof lifeReadingSchema>>;
